import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import statusApiFunctions from '../../api/status';
import { ComponentStatus, ComponentStatusCheck, ComponentStatusCheckResult } from '../../types/componentStatus';
import { Check, Close, Help } from '@mui/icons-material';
import * as _ from 'lodash';
import { useState } from 'react';

import useComponentStatusData from '../../hooks/useComponentStatusData';

import styles from './index.module.css';

const ComponentStatuses = () => {
  const [statusComponents, setStatusComponents] = useState<Array<ComponentStatusCheck>>();
  const [lastRefreshTime, setLastRefreshTime] = useState<string>();
  const [showDescModal, setShowDescModal] = useState<boolean>(false);
  const [modalDescription, setModalDescription] = useState<ComponentStatusCheckResult>();

  const syncStatusChecks = (data: any) => {
    if (Array.isArray(data)) {
      data.forEach((x) => (x.isLoadingStatus = true));
      setStatusComponents(data);
      data.forEach(async (endpoint) => {
        const res = (await statusApiFunctions.checkComponentStatus(
          endpoint.componentStatusCheckId,
        )) as ComponentStatusCheckResult;
        endpoint.isLoadingStatus = false;
        endpoint.statusResult = res;
        setStatusComponents([...data]);
      });
      setLastRefreshTime(new Date().toLocaleString());
    }
  };

  const { isLoading, isError, refetch } = useComponentStatusData(syncStatusChecks);

  const openDescriptionModal = (component: ComponentStatusCheck) => {
    setModalDescription(component.statusResult);
    setShowDescModal(true);
  };

  const descriptionFromBuildDate = (buildDate: string | null | undefined): string => {
    let tokens = _.chain(buildDate || '')
      .replace(')', '')
      .split('(')
      .value();
    return tokens.length ? tokens[1] : '';
  };

  const modalStyle = {
    position: 'absolute',
    overflowY: 'scroll',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
  };

  const buildTables = (list: Array<ComponentStatusCheck>) => {
    const map = new Map();
    list.forEach((item: ComponentStatusCheck) => {
      const collection = map.get(item.groupName);
      if (!collection) {
        map.set(item.groupName, [item]);
      } else {
        collection.push(item);
      }
    });

    const tables = [];
    map.forEach((value: Array<ComponentStatusCheck>, key) => {
      tables.push(
        <Box key={key} className={styles.componentGroups}>
          <Box color="text.secondary" className={styles.componentGroupName}>
            {key}
          </Box>
          <TableContainer className={styles.statusCheckTable}>
            <Table size="small" aria-label="ComponentStatusCheckTable">
              <TableHead>
                <TableRow>
                  <TableCell align="center">Name</TableCell>
                  <TableCell align="center">Build Version</TableCell>
                  <TableCell align="center">Build Date</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {value.map((x, i) => (
                  <TableRow
                    key={x.componentStatusCheckId}
                    role="tableBodyRow"
                    onClick={() => openDescriptionModal(x)}
                    data-id={x.componentStatusCheckId}
                    sx={{ width: '100%' }}>
                    <TableCell padding="none" align="left" className={styles.firstColumn}>
                      {x.name}
                    </TableCell>
                    <TableCell padding="none" align="center">
                      {x.statusResult?.buildVersion}
                    </TableCell>
                    <TableCell padding="none" align="center">
                      <Tooltip title={x.statusResult?.buildDate || ''}>
                        <Box>{descriptionFromBuildDate(x.statusResult?.buildDate)}</Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell padding="none" align="center">
                      {!x.isLoadingStatus && x.statusResult?.status === ComponentStatus.Healthy ? (
                        <Check color="success" fontSize="large" />
                      ) : (
                        ''
                      )}
                      {!x.isLoadingStatus && x.statusResult?.status === ComponentStatus.Unhealthy ? (
                        <Close color="error" fontSize="large" />
                      ) : (
                        ''
                      )}
                      {!x.isLoadingStatus && x.statusResult?.status === ComponentStatus.Unknown ? (
                        <Help color="error" fontSize="large" />
                      ) : (
                        ''
                      )}
                      {x.isLoadingStatus ? <CircularProgress size={'2.2rem'} /> : ''}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>,
      );
    });

    if (tables.length < 1) {
      tables.push(
        <Table size="small" aria-label="ComponentStatusCheckTable">
          <TableBody>
            <TableRow key={'Loading'}>
              <TableCell padding="none" align="right">
                {isLoading ? 'Loading...' : 'An error has occurred loading the status check endpoint statuses'}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>,
      );
    }

    return tables;
  };

  return (
    <Box className={styles.statusCheckDashboardContainer}>
      <Box color="text.secondary" className={styles.panelLabel}>
        Component Status Checks
      </Box>
      {!isLoading && !isError && statusComponents ? (
        <>{buildTables(statusComponents)}</>
      ) : (
        <TableContainer className={styles.statusCheckTable}>
          <Table size="small" aria-label="ComponentStatusCheckTable">
            <TableBody>
              <TableRow key={'Loading'}>
                <TableCell padding="none" align="right">
                  {isLoading ? 'Loading...' : 'An error has occurred loading the status check endpoint statuses'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!isLoading && !isError && statusComponents && (
        <Box className={styles.refreshContainer}>
          <Box className={styles.refreshText}>Last refreshed: {lastRefreshTime}</Box>
          <Button className={styles.refreshButton} onClick={() => refetch()} aria-label="RefreshButton">
            Refresh All
          </Button>
        </Box>
      )}
      <Modal
        open={showDescModal}
        onClose={() => setShowDescModal(false)}
        aria-labelledby="ComponentStatusCheckResult"
        aria-describedby="ComponentStatusCheckDescription">
        <Box className={styles.descModal} sx={modalStyle}>
          <Box>
            <Box sx={{ fontWeight: 'bold' }}>Build Date:</Box> {modalDescription?.buildDate}
          </Box>
          <Box>
            <Box sx={{ fontWeight: 'bold' }}>Build Version:</Box> {modalDescription?.buildVersion}
          </Box>
          <Box>
            <Box sx={{ fontWeight: 'bold' }}>Duration:</Box> {modalDescription?.durationMS} ms
          </Box>
          <Box>
            <Box sx={{ fontWeight: 'bold' }}>Details:</Box> <pre>{modalDescription?.details}</pre>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ComponentStatuses;
